import { useState } from 'react'
import { isValidTimezone } from '@gogrov/shared'
import { useCreateClient, useUpdateClient } from '@/hooks/useAdminActions'
import { AdminError } from './AdminError'
import type { Client } from '@/types/database'

const DEFAULT_ACCENT = '#c88338'

const COMMON_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
]

interface ClientFormModalProps {
  /** Omit to create; pass a client to edit it. */
  client?: Client
  onClose: () => void
  onCreated?: (client: Client) => void
}

export function ClientFormModal({ client, onClose, onCreated }: ClientFormModalProps) {
  const isEdit = !!client
  const create = useCreateClient()
  const update = useUpdateClient()
  const pending = create.isPending || update.isPending

  const [slug, setSlug] = useState(client?.slug ?? '')
  const [displayName, setDisplayName] = useState(client?.display_name ?? '')
  const [cityState, setCityState] = useState(client?.city_state ?? '')
  const [state, setState] = useState(client?.state ?? '')
  const [timezone, setTimezone] = useState(client?.timezone ?? 'America/Denver')
  const [accent, setAccent] = useState(client?.accent_color ?? DEFAULT_ACCENT)
  const [active, setActive] = useState(client?.active ?? true)
  const [places, setPlaces] = useState('')

  const tzValid = isValidTimezone(timezone)
  const canSubmit =
    displayName.trim() && cityState.trim() && state.trim() && tzValid && (isEdit || slug.trim())

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit || pending) return

    if (isEdit) {
      update.mutate(
        {
          clientId: client.id,
          input: { display_name: displayName, city_state: cityState, state, timezone, accent_color: accent, active },
        },
        { onSuccess: onClose }
      )
    } else {
      create.mutate(
        {
          slug, display_name: displayName, city_state: cityState, state, timezone, accent_color: accent,
          places: places.split(',').map((p) => p.trim()).filter(Boolean),
        },
        {
          onSuccess: (created) => {
            onCreated?.(created)
            onClose()
          },
        }
      )
    }
  }

  return (
    <div className="admin-modal-scrim" onClick={onClose}>
      <div className="admin-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h2 className="admin-modal__title">{isEdit ? `Edit ${client.display_name}` : 'New client'}</h2>
          <button className="admin-modal__close" aria-label="Close" onClick={onClose}>×</button>
        </div>

        <form className="admin-form" onSubmit={submit}>
          {!isEdit && (
            <label className="admin-field">
              <span className="admin-field__label">Slug</span>
              <input
                className="admin-input mono"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                placeholder="park-city"
                required
              />
              <span className="admin-field__hint">Permanent. Used to link signups to this client.</span>
            </label>
          )}

          <label className="admin-field">
            <span className="admin-field__label">Display name</span>
            <input className="admin-input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} required />
          </label>

          <div className="admin-field-row">
            <label className="admin-field">
              <span className="admin-field__label">City, State</span>
              <input className="admin-input" value={cityState} onChange={(e) => setCityState(e.target.value)} required />
            </label>
            <label className="admin-field admin-field--narrow">
              <span className="admin-field__label">State</span>
              <input
                className="admin-input"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                maxLength={2}
                required
              />
            </label>
          </div>

          <label className="admin-field">
            <span className="admin-field__label">Timezone</span>
            <input
              className="admin-input mono"
              list="admin-timezones"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              required
            />
            <datalist id="admin-timezones">
              {COMMON_TIMEZONES.map((tz) => <option key={tz} value={tz} />)}
            </datalist>
            <span className={`admin-field__hint ${tzValid ? '' : 'admin-field__hint--error'}`}>
              {tzValid ? 'Seed weeks and headline pulls are keyed to this timezone.' : 'Not a valid IANA timezone.'}
            </span>
          </label>

          <div className="admin-field-row">
            <label className="admin-field admin-field--narrow">
              <span className="admin-field__label">Accent colour</span>
              <input className="admin-colour" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} />
            </label>
            {isEdit && (
              <label className="admin-field admin-checkbox">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                <span>Active</span>
              </label>
            )}
          </div>

          {!isEdit && (
            <label className="admin-field">
              <span className="admin-field__label">Coverage areas</span>
              <input
                className="admin-input"
                value={places}
                onChange={(e) => setPlaces(e.target.value)}
                placeholder="Park City, Heber, Kamas"
              />
              <span className="admin-field__hint">
                Comma-separated. All 11 topics are seeded at 30% and can be tuned after.
              </span>
            </label>
          )}

          <AdminError error={create.error ?? update.error} />

          <div className="admin-modal__actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={pending}>Cancel</button>
            <button type="submit" className="btn-primary btn--amber" disabled={!canSubmit || pending}>
              {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Create client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
